interface PickerStore {
  id: number
  /** Three-digit store code, e.g. `022`. */
  code: string
  name: string | null
}

interface ShopPickerProps {
  stores: PickerStore[]
  /** Exactly one shop is open at a time — these are not filters. */
  selectedId: number | null
  onSelect: (id: number) => void
  /** What the selected shop controls; defaults to the embed wording. */
  hint?: string
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors'
const PILL_ACTIVE =
  'border-salsa-500 bg-salsa-500 text-white shadow-sm hover:bg-salsa-600 dark:border-salsa-500'
const PILL_IDLE =
  'border-slate-200 bg-white text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-slate-200'

/**
 * Single-select shop switcher for a per-shop embed.
 *
 * Unlike `ShopPills` (a multi-select filter with All/Clear), this is a **radio group**:
 * every shop has its own dashboard, so exactly one can be open. Each shop is a real
 * button with `role="radio"`/`aria-checked`, which keeps it keyboard- and
 * screen-reader-usable, and the selected pill uses the same orange as the rest of the app.
 */
export function ShopPicker({ stores, selectedId, onSelect, hint }: ShopPickerProps) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Shop
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {hint ?? `${stores.length} shops in your scope · one dashboard each`}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label="Shop"
        className="flex max-h-40 flex-wrap gap-1.5 overflow-auto pr-1"
      >
        {stores.map((store) => {
          const active = store.id === selectedId
          return (
            <button
              key={store.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onSelect(store.id)}
              className={`${PILL_BASE} ${active ? PILL_ACTIVE : PILL_IDLE}`}
              title={store.name ?? `Shop ${store.code}`}
            >
              <span
                className={`font-mono text-[10px] ${active ? 'text-white/70' : 'text-slate-400 dark:text-slate-500'}`}
              >
                #{store.code}
              </span>
              <span className="max-w-[9rem] truncate">{store.name ?? `Shop ${store.code}`}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
